import React, { ReactElement } from 'react';
import { PerformanceCheckDataValue } from '../../../models/performance_check';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_CUSTOMER_COLORS } from '../../../utils';
import { Card } from 'react-bootstrap';
import {
  segmentColor,
  segmentTextColor,
  SEGMENT_LABEL,
} from '../trees/nodeSegment';
import { TIERS } from '../trees/treePerformance';
import '../trees/treePerformanceCharts.css';

/** Below this share of the track a bar cannot hold its own figure. */
const MIN_INSIDE_LABEL = 24;

const dollars = (value: number) =>
  `$${Math.round(value).toLocaleString('en-US')}`;

interface PredictedLeadValueProps {
  data: PerformanceCheckDataValue;
  /**
   * Draw the tiers as the rate bars the rest of the new Performance page uses:
   * best tier on top, whole dollars, the figure outside a bar too short to
   * hold it. The legacy page leaves it off and keeps the recharts chart.
   */
  tierBars?: boolean;
}

/**
 * The headline ratio, or a sentence that does not divide by zero. A Low tier
 * with no Closed Won revenue is common on a good model, and dividing by it
 * printed "$Infinityx".
 */
function headline(veryGood: number, low: number): ReactElement {
  if (veryGood <= 0) {
    return (
      <>Your &apos;Very Good&apos; leads brought in no Closed Won revenue.</>
    );
  }
  if (low <= 0) {
    return (
      <>
        Your &apos;Very Good&apos; leads are worth <b>{dollars(veryGood)}</b>{' '}
        each, and your &apos;Low&apos; leads brought in no revenue.
      </>
    );
  }
  const ratio = veryGood / low;
  return (
    <>
      Your &apos;Very Good&apos; leads are worth{' '}
      <b>{ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1)}x</b> more than
      your &apos;Low&apos; leads.
    </>
  );
}

function TierBars({ data }: { data: PerformanceCheckDataValue }) {
  const values = TIERS.map((segment) => Number(data?.[segment]) || 0);
  const best = Math.max(...values);
  return (
    <Card className="h-100">
      <Card.Body>
        <Card.Title>Predicted Lead Value</Card.Title>
        <p className="tpf-headline">
          {headline(values[0], values[values.length - 1])}
        </p>
        {TIERS.map((segment, index) => {
          const width = best > 0 ? (values[index] / best) * 100 : 0;
          return (
            <div className="tpf-rate-row" key={segment}>
              <span className="tpf-rate-label">{SEGMENT_LABEL[segment]}</span>
              <div className="tpf-rate-track">
                <span
                  className="tpf-rate-fill"
                  style={{
                    width: `${width}%`,
                    background: segmentColor(segment) ?? undefined,
                  }}
                >
                  {width >= MIN_INSIDE_LABEL && (
                    <span
                      className="tpf-rate-value"
                      style={{ color: segmentTextColor(segment) ?? undefined }}
                    >
                      {dollars(values[index])}
                    </span>
                  )}
                </span>
                {width < MIN_INSIDE_LABEL && (
                  <span className="tpf-rate-value tpf-rate-value--outside">
                    {dollars(values[index])}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
}

export default function PredictedLeadValue({
  data,
  tierBars,
}: PredictedLeadValueProps): ReactElement {
  if (tierBars) return <TierBars data={data} />;

  const formattedData = [
    { name: 'low', value: Number((data?.low ?? 0).toFixed(2)) },
    { name: 'medium', value: Number((data?.medium ?? 0).toFixed(2)) },
    { name: 'good', value: Number((data?.good ?? 0).toFixed(2)) },
    { name: 'very good', value: Number((data?.['very good'] ?? 0).toFixed(2)) },
  ];

  const rate = Math.round((data?.['very good'] ?? 0) / (data?.low ?? 0));

  return (
    <Card className="h-100">
      <Card.Body>
        <Card.Title>Predicted Lead Value</Card.Title>
        <Card.Text>
          Your &apos;Very Good&apos; leads are worth <b>${rate}x</b> more than
          your &apos;Low&apos; leads.
        </Card.Text>
        <div style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={formattedData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 'dataMax']} />
              <YAxis dataKey="name" type="category" />
              <Tooltip formatter={(value) => `$${value}`} />
              <Bar dataKey="value">
                {formattedData.map(({ name }) => (
                  <Cell
                    key={name}
                    fill={CHART_CUSTOMER_COLORS.customer_fit[name].background}
                  />
                ))}
                <LabelList
                  dataKey="value"
                  fill="white"
                  formatter={(value: number) => `$${value}`}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
}
